import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { FormattedNumberInput } from '@rd-web-markets/shared/dist/util/FormattedNumberInput';
import { useSelector } from 'react-redux';
import claimEmployeeRndApportionmentsService from '../../services/claim/claim_employee_rnd_apportionments.service';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import ApportionmentsList from '../ApportionmentsList';
import ChangeHighlightedImmutableUnderReviewControl from '@rd-web-markets/shared/dist/util/reviewEditControls/ChangeHighlightedImmutableUnderReviewControl';
import { DeleteButton } from '@rd-web-markets/shared/dist/util/buttons';
export function SubcontractorRow(_ref) {
  let {
    sortedClaimProjectReports,
    index,
    claim,
    handleDelete,
    claimSubcontractors,
    setClaimSubcontractors,
    refreshClaim,
    handleError,
    claimSubcontractorsService,
    headerScrollPosition,
    isShowInvolvement
  } = _ref;
  const [hasError, setHasError] = useState(false);
  const claimEmployeeRndApportRef = useRef(null);
  const user = useSelector(state => state.auth.user);
  const claimSubcontractor = useMemo(() => {
    return {
      ...claimSubcontractors[index]
    };
  }, [claimSubcontractors, index]);
  const dispatch = useDispatch();
  useEffect(() => {
    if (claimEmployeeRndApportRef.current) {
      claimEmployeeRndApportRef.current.scrollTo({
        left: headerScrollPosition
      });
    }
  }, [claimEmployeeRndApportRef, headerScrollPosition]);
  const updateSubcontractors = useCallback(subcontractor => {
    const newSubcontractors = [...claimSubcontractors];
    newSubcontractors[index] = {
      ...subcontractor
    };
    setClaimSubcontractors([...newSubcontractors]);
  }, [claimSubcontractors, index, setClaimSubcontractors]);
  const handleApportionmentChange = useErrorHandling(useCallback(async (subcontractor, apportionment) => {
    const oldApportionment = subcontractor.claim_employee_rnd_apportionments.find(a => a.claim_project_report_id === apportionment.claim_project_report_id);
    const index = subcontractor.claim_employee_rnd_apportionments.indexOf(oldApportionment);
    const newApportionments = [...subcontractor.claim_employee_rnd_apportionments];
    if (index === -1) {
      newApportionments.push(apportionment);
    } else {
      newApportionments[index] = {
        ...apportionment
      };
    }
    const updatedSubcontractor = await claimSubcontractorsService.get(subcontractor);
    updatedSubcontractor.claim_employee_rnd_apportionments = newApportionments;
    updateSubcontractors(updatedSubcontractor);
  }, [updateSubcontractors]));
  const handleChange = useCallback(event => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    claimSubcontractor[event.target.name] = value;
    updateSubcontractors(claimSubcontractor);
  }, [updateSubcontractors, claimSubcontractor]);
  const saveChange = useCallback(() => {
    let method;
    if (claimSubcontractor.id) {
      method = 'update';
    } else {
      method = 'create';
    }
    claimSubcontractorsService[method](claimSubcontractor).then(updatedSubcontractor => {
      setHasError(false);
      updateSubcontractors(updatedSubcontractor);
      refreshClaim();
    }).catch(e => {
      setHasError(true);
      dispatch(handleError(e));
    });
  }, [updateSubcontractors, claimSubcontractor, dispatch, refreshClaim]);
  const hasPermissions = () => {
    return user.account_type !== 'customer' && user.account_type !== 'accountant';
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
    className: hasError ? 'bg-warning' : ''
  }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions(),
    name: "name",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions(),
    name: "description_of_activity",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions(),
    name: "net_amount",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || !hasPermissions(),
    name: "capitalised_amount",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Check,
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    name: "connected_party",
    disabled: claim.financials_locked || !hasPermissions(),
    onChange: async e => {
      await handleChange(e);
      saveChange();
    }
  })), isShowInvolvement && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    disabled: claim.financials_locked || user.account_type === 'customer',
    name: "direct_rnd_apportionment",
    type: "text",
    size: "sm"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    onChange: handleChange,
    onBlur: saveChange,
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    disabled: claim.financials_locked || user.account_type === 'customer',
    name: "indirect_rnd_apportionment",
    type: "text",
    size: "sm"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    decimalScale: 1,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || claim.claim_group.project_methodology !== 'employee_basis' || !hasPermissions(),
    name: "rnd_apportionment",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  }))), claim.claim_group.project_methodology === 'project_basis' && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ApportionmentsList, {
    claim: claim,
    sortedClaimProjectReports: sortedClaimProjectReports,
    headerScrollPosition: headerScrollPosition,
    claimEmployeeRndApportRef: claimEmployeeRndApportRef,
    handleApportionmentChange: handleApportionmentChange,
    claimEmployeeRndApportionmentsService: claimEmployeeRndApportionmentsService,
    model: claimSubcontractor,
    apportionmentable_type: "UkSection::ClaimSubcontractor",
    isShowInvolvement: isShowInvolvement
  })), !isShowInvolvement && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    decimalScale: 1,
    onChange: handleChange,
    onBlur: saveChange,
    disabled: claim.financials_locked || claim.claim_group.project_methodology !== 'employee_basis' || !hasPermissions(),
    name: "rnd_apportionment",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: Form.Control,
    disabled: true || !hasPermissions(),
    name: "allocation",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    disabled: true,
    name: "rnd_amount",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    disabled: true || !hasPermissions(),
    name: "pnl_expenditure",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(ChangeHighlightedImmutableUnderReviewControl, {
    as: FormattedNumberInput,
    disabled: true || !hasPermissions(),
    name: "s1308",
    object: {
      ...claimSubcontractor,
      claim: {
        ...claim,
        ...claimSubcontractor.claim
      }
    },
    size: "sm",
    type: "text"
  })), /*#__PURE__*/React.createElement("td", null, hasPermissions() && /*#__PURE__*/React.createElement(DeleteButton, {
    style: {
      height: '28px'
    },
    iconClassName: "text-danger fa-2x",
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set || user.account_type === 'customer',
    isImmutableUnderReview: true,
    onClick: handleDelete
  }))));
}